import {
  arrayUnion,
  collection,
  doc,
  getDocs,
  limit,
  query,
  runTransaction,
  Unsubscribe,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { db } from 'shared/services/firebase';
import { subscribeToQuery, getQueryOnce } from 'shared/services/firestore';
import { mapExpertJobMetric, ExpertJobMetric } from 'domains/jobs/model/expertJobMetric';
import { UserAccount } from 'shared/types/userAccount';
import { Job } from 'shared/types/job';
import { mapWorkerTask, WorkerTask } from 'shared/types/task';
import {
  ActiveJobAssignmentProjection,
  buildActiveJobAssignmentProjection,
  countJobTasks,
} from 'domains/jobs/application/assignedJobs';
import { IdempotentWriteResult } from 'domains/jobs/model/submission';

/**
 * Expert "assigned" data from `expert_job_metrics` (queried by `expertId`, which
 * is the user_accounts doc id — NOT the firebase uid). Mirrors aligned's
 * getActiveExpertJobMetricsByExpertId / getPastDueExpertJobMetricsByExpertId.
 */
const COLLECTION = 'expert_job_metrics';

const TASKS_COLLECTION = 'tasks';

function chunks<T>(values: T[], size: number): T[][] {
  const result: T[][] = [];
  for (let index = 0; index < values.length; index += size) {
    result.push(values.slice(index, index + size));
  }
  return result;
}

export function subscribeToActiveExpertJobMetrics(
  expertId: string,
  onNext: (metrics: ExpertJobMetric[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, COLLECTION),
    where('expertId', '==', expertId),
    where('jobIsActive', '==', true),
  );
  return subscribeToQuery(q, mapExpertJobMetric, onNext, {
    context: 'subscribeToActiveExpertJobMetrics',
    onError,
  });
}

/** One-shot active EJM read used by the source assigned-jobs view. */
export function getActiveExpertJobMetricsByExpertId(expertId: string): Promise<ExpertJobMetric[]> {
  return getQueryOnce(
    query(
      collection(db, COLLECTION),
      where('expertId', '==', expertId),
      where('jobIsActive', '==', true),
    ),
    mapExpertJobMetric,
  );
}

async function getTasksByExpertIdAndJobIds(expertId: string, jobIds: string[]): Promise<WorkerTask[]> {
  const uniqueJobIds = Array.from(new Set(jobIds.filter(Boolean)));
  if (uniqueJobIds.length === 0) return [];

  const rows: WorkerTask[] = [];
  for (const jobIdChunk of chunks(uniqueJobIds, 30)) {
    rows.push(
      ...(await getQueryOnce(
        query(
          collection(db, TASKS_COLLECTION),
          where('deletedAt', '==', null),
          where('expertId', '==', expertId),
          where('jobId', 'in', jobIdChunk),
        ),
        mapWorkerTask,
      )),
    );
  }
  return rows;
}

async function getUnassignedTasksByJobIds(jobIds: string[]): Promise<WorkerTask[]> {
  const uniqueJobIds = Array.from(new Set(jobIds.filter(Boolean)));
  if (uniqueJobIds.length === 0) return [];

  const rows: WorkerTask[] = [];
  for (const jobIdChunk of chunks(uniqueJobIds, 30)) {
    rows.push(
      ...(await getQueryOnce(
        query(
          collection(db, TASKS_COLLECTION),
          where('deletedAt', '==', null),
          where('jobId', 'in', jobIdChunk),
          where('expertId', '==', null),
          where('taskStatus', '==', 'unreviewed'),
        ),
        mapWorkerTask,
      )),
    );
  }
  return rows;
}

async function getTasksByJobIds(jobIds: string[]): Promise<WorkerTask[]> {
  const uniqueJobIds = Array.from(new Set(jobIds.filter(Boolean)));
  if (uniqueJobIds.length === 0) return [];

  const rows: WorkerTask[] = [];
  for (const jobIdChunk of chunks(uniqueJobIds, 30)) {
    rows.push(
      ...(await getQueryOnce(
        query(
          collection(db, TASKS_COLLECTION),
          where('deletedAt', '==', null),
          where('jobId', 'in', jobIdChunk),
        ),
        mapWorkerTask,
      )),
    );
  }
  return rows;
}

/**
 * Port of aligned's `TaskService.getActiveJobAssignmentsForExpert`.
 *
 * Active jobs are split by actual task eligibility, not only by the EJM flag:
 * already-seen clientTaskIds, expert-pool restrictions, max-task limits, due
 * dates, and assigned work all participate in the result. The source also
 * persists an EJM as ineligible when the verification pass revokes it; this
 * method keeps that side effect limited to the same two fields.
 */
export async function getActiveExpertJobAssignmentsForExpert(
  expert: UserAccount,
): Promise<ActiveJobAssignmentProjection> {
  const activeMetrics = await getActiveExpertJobMetricsByExpertId(expert.id);
  const eligibleCandidates = activeMetrics.filter((metric) => metric.isEligibleForTaskAssignment);
  const jobIds = eligibleCandidates.map((metric) => metric.jobId);

  const [assignedTasks, unassignedTasks] = await Promise.all([
    getTasksByExpertIdAndJobIds(expert.id, jobIds),
    getUnassignedTasksByJobIds(jobIds),
  ]);
  const projection = buildActiveJobAssignmentProjection({
    expert,
    activeMetrics,
    assignedTasks,
    unassignedTasks,
  });
  const allJobTasks = await getTasksByJobIds(
    projection.eligibleEjmSummaries.map((summary) => summary.ejm.jobId),
  );

  for (const metricId of projection.metricsToMarkIneligible) {
    await updateDoc(doc(db, COLLECTION, metricId), {
      isEligibleForTaskAssignment: false,
      lastUpdatedAt: projection.eligibilityUpdatedAt,
    });
  }

  return {
    ...projection,
    taskCountsByJobId: countJobTasks(allJobTasks),
  };
}

/** The single metric row for an expert + job (used to apply task-completion). */
export async function getExpertJobMetricByExpertIdAndJobId(
  expertId: string,
  jobId: string,
): Promise<ExpertJobMetric | null> {
  const rows = await getQueryOnce(
    query(
      collection(db, COLLECTION),
      where('expertId', '==', expertId),
      where('jobId', '==', jobId),
      limit(1),
    ),
    mapExpertJobMetric,
  );
  return rows[0] ?? null;
}

/**
 * Apply the source EJM completion update for one task. The task id is retained
 * on the metric as an idempotency ledger and checked inside the same
 * transaction as the counter update. This is the only safe place to retry the
 * source's `tasksAssigned-- / tasksCompleted++` mutation from a browser.
 */
export async function accrueExpertJobMetricForTask(
  metric: ExpertJobMetric,
  taskId: string,
  secondsWorked: number,
  earnings: number,
): Promise<IdempotentWriteResult> {
  const now = new Date().toISOString();
  return runTransaction(db, async (tx): Promise<IdempotentWriteResult> => {
    const metricRef = doc(db, COLLECTION, metric.id);
    const snapshot = await tx.get(metricRef);
    if (!snapshot.exists()) return 'unavailable';

    const raw = snapshot.data() as Record<string, unknown>;
    const processedTaskIds = Array.isArray(raw.processedTaskIds)
      ? raw.processedTaskIds.filter((value): value is string => typeof value === 'string')
      : [];
    if (processedTaskIds.includes(taskId)) return 'alreadyProcessed';

    const current = mapExpertJobMetric(snapshot.id, raw);
    tx.update(metricRef, {
      tasksAssigned: current.tasksAssigned - 1,
      tasksCompleted: current.tasksCompleted + 1,
      secondsWorked: current.secondsWorked + secondsWorked,
      earnings: current.earnings + earnings,
      lastTaskCompletedAt: now,
      lastUpdatedAt: now,
      processedTaskIds: arrayUnion(taskId),
    });
    return 'updated';
  });
}

/** Past-due jobs: due before now and not deprecated. One-time (depends on now). */
export function getPastDueExpertJobMetrics(expertId: string): Promise<ExpertJobMetric[]> {
  const nowIso = new Date().toISOString();
  const q = query(
    collection(db, COLLECTION),
    where('expertId', '==', expertId),
    where('jobDueAt', '<', nowIso),
    where('deprecatedOn', '==', null),
  );
  return getQueryOnce(q, mapExpertJobMetric);
}

/**
 * Every (non-deprecated) `expert_job_metrics` row for the expert — feeds the
 * expert-home 12-month chart's job/task counts. Ported from aligned's
 * `TaskService.getExpertJobMetricsByExpertId`.
 */
export function getAllExpertJobMetrics(expertId: string): Promise<ExpertJobMetric[]> {
  const q = query(
    collection(db, COLLECTION),
    where('expertId', '==', expertId),
    where('deprecatedOn', '==', null),
  );
  return getQueryOnce(q, mapExpertJobMetric);
}

export async function getExpertJobMetricsByJobIds(jobIds: string[]): Promise<ExpertJobMetric[]> {
  const uniqueJobIds = Array.from(new Set(jobIds.filter(Boolean)));
  const rows: ExpertJobMetric[] = [];
  for (let offset = 0; offset < uniqueJobIds.length; offset += 30) {
    rows.push(...await getQueryOnce(
      query(collection(db, COLLECTION), where('jobId', 'in', uniqueJobIds.slice(offset, offset + 30))),
      mapExpertJobMetric,
    ));
  }
  return rows.filter((row) => !row.deprecatedOn);
}

/**
 * Reads the current expert's metrics for a campaign's linked jobs. This is
 * the query shape used by aligned's
 * `getExpertJobMetricsByExpertIdAndJobIdList`: the caller owns the
 * campaign-job list, while Firestore scopes the metric rows to both the
 * expert and those jobs. Deprecated rows are intentionally left in the
 * result so the campaign-detail boundary can apply aligned's explicit
 * post-read filter.
 */
export async function getExpertJobMetricsByExpertIdAndJobIds(
  expertId: string,
  jobIds: string[],
): Promise<ExpertJobMetric[]> {
  const uniqueJobIds = Array.from(new Set(jobIds.filter(Boolean)));
  if (uniqueJobIds.length === 0) return [];

  const rows: ExpertJobMetric[] = [];
  for (let offset = 0; offset < uniqueJobIds.length; offset += 30) {
    rows.push(
      ...(await getQueryOnce(
        query(
          collection(db, COLLECTION),
          where('expertId', '==', expertId),
          where('jobId', 'in', uniqueJobIds.slice(offset, offset + 30)),
        ),
        mapExpertJobMetric,
      )),
    );
  }
  return rows;
}

export type AssignExpertToJobResult = 'assigned' | 'alreadyAssigned';

export function buildExpertJobMetricForAssignment(params: {
  id: string;
  expert: UserAccount;
  job: Job;
  hourlyRate: number | null;
  task: Record<string, unknown>;
  nowIso: string;
  fallbackDueAt: string;
  /** Explicit due date used by campaign-stage assignment. When supplied it
   * mirrors TaskService's `jobDueAt` argument exactly, including an expired
   * value (the caller can then treat the assignment as unsuccessful). */
  assignmentDueAt?: string | null;
}): Record<string, unknown> {
  const { id, expert, job, task, nowIso, fallbackDueAt } = params;
  const jobDueAt = params.assignmentDueAt !== undefined
    ? params.assignmentDueAt
    : job.dueAt && new Date(job.dueAt).getTime() > new Date(nowIso).getTime() ? job.dueAt : fallbackDueAt;
  return {
    id,
    firebaseId: expert.firebaseId,
    expertId: expert.id,
    expertEmail: expert.email,
    jobId: job.id,
    jobName: job.name,
    jobDueAt,
    projectId: job.projectId,
    customerId: job.customerId,
    createdAt: nowIso,
    reviewedOn: null,
    deprecatedOn: null,
    maxTaskLimit: job.maxTasksPerExpert,
    hourlyRate: params.hourlyRate ?? expert.currentHourlyWageBid ?? 15_000_000,
    taskPaymentType: typeof task.taskPaymentType === 'string' ? task.taskPaymentType : job.taskPaymentType,
    taskType: typeof task.taskType === 'string' ? task.taskType : null,
    taskCategory: typeof task.taskCategory === 'string' ? task.taskCategory : null,
    jobIsActive: true,
    isEligibleForTaskAssignment: true,
    tasksAssigned: 0,
    tasksCompleted: 0,
    tasksFlagged: 0,
    tasksSkipped: 0,
    secondsWorked: 0,
    earnings: 0,
  };
}

/**
 * Port of the durable portion of TaskService.assignExpertsToJob used by
 * campaign stage entry: create one EJM and one notification. Task reservation
 * remains lazy when the expert opens the job, matching the existing React
 * task-assignment queue.
 */
export async function assignExpertToJob(params: {
  expert: UserAccount;
  job: Job;
  hourlyRate: number | null;
  assignmentDueAt?: string | null;
}): Promise<AssignExpertToJobResult> {
  const { expert, job } = params;
  const existing = await getExpertJobMetricByExpertIdAndJobId(expert.id, job.id);
  if (existing && !existing.deprecatedOn) return 'alreadyAssigned';

  const taskSnap = await getDocs(query(collection(db, 'tasks'), where('jobId', '==', job.id), limit(1)));
  const task = taskSnap.docs[0]?.data();
  if (!task) throw new Error(`Job ${job.id} has no associated tasks.`);

  const now = new Date().toISOString();
  if (params.assignmentDueAt && new Date(params.assignmentDueAt).getTime() <= new Date(now).getTime()) {
    throw new Error(`Job ${job.id} is past its due date.`);
  }
  const fallbackDueAt = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();
  const metricRef = doc(collection(db, COLLECTION));
  const notificationRef = doc(collection(db, 'notifications'));
  const metric = buildExpertJobMetricForAssignment({ id: metricRef.id, expert, job, hourlyRate: params.hourlyRate, task, nowIso: now, fallbackDueAt, assignmentDueAt: params.assignmentDueAt });
  const notification = {
    id: notificationRef.id,
    firebaseId: expert.firebaseId,
    userAccountId: expert.id,
    createdOn: now,
    userType: 'EXPERT',
    status: 'unread',
    readOn: null,
    deprecatedOn: null,
    message: `You have been assigned to a new job: ${job.name}.`,
  };
  const batch = writeBatch(db);
  batch.set(metricRef, metric);
  batch.set(notificationRef, notification);
  await batch.commit();
  return 'assigned';
}
